/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { computed, inject, Injectable, signal } from '@angular/core';
import { AuthenticationService } from '@alfresco/adf-core';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { MyTasksService } from '../workflow/my-tasks.service';
import { ExpiryService } from '../expiry/expiry.service';

export type AppNotificationKind = 'task' | 'pool' | 'expiry';

/** One row in the bell menu — a live view of real work, not a stored event log. */
export interface AppNotification {
  /** Stable across refreshes (`task:<id>` / `pool:<id>` / `expiry:<nodeId>`) so the seen-set knows it. */
  id: string;
  kind: AppNotificationKind;
  title: string;
  /** Context line — document name or the date the thing comes due. */
  detail: string;
  /** ISO date for sorting; tasks fall back to their process start. */
  at?: string;
  /** Due date passed / expiry passed — the row styles itself red. */
  overdue?: boolean;
  route: string[];
}

/**
 * The bell's data.
 *
 * There is no notifications table anywhere in this stack, and writing one
 * would lie about events it never saw. Instead this service aggregates the
 * three feeds that are already real: tasks assigned to the person, pooled
 * tasks they can claim, and documents past or near their `cm:to` date. Each
 * row is a stable id, so a "seen" marker survives reloads — the badge counts
 * rows that are new since the menu was last opened, and opening the menu is
 * what marks them seen.
 */
@Injectable({ providedIn: 'root' })
export class NotificationsService {
  private readonly myTasks = inject(MyTasksService);
  private readonly expiry = inject(ExpiryService);
  private readonly auth = inject(AuthenticationService);

  /** localStorage keeps the seen-set per signed-in account. */
  private static readonly SEEN_KEY = 'aca.notif.seen';
  /** Old ids fall off so the set does not grow forever. */
  private static readonly SEEN_MAX = 300;

  readonly items = signal<AppNotification[]>([]);
  readonly seenIds = signal<ReadonlySet<string>>(new Set(this.loadSeen()));

  /** Rows the person has not opened the menu since they appeared. */
  readonly unread = computed(() => this.items().filter((item) => !this.seenIds().has(item.id)).length);

  refresh(): void {
    forkJoin({
      todo: this.myTasks.todo().pipe(catchError(() => of([]))),
      pooled: this.myTasks.pooled().pipe(catchError(() => of([]))),
      expiring: this.expiry.listWithExpiry(200).pipe(catchError(() => of([])))
    }).subscribe(({ todo, pooled, expiring }) => {
      const items: AppNotification[] = [
        ...todo.map((task) => ({
          id: `task:${task.id}`,
          kind: 'task' as const,
          title: task.name,
          detail: task.docName || task.step || '',
          at: task.dueAt || task.startedAt,
          overdue: !!task.dueAt && new Date(task.dueAt).getTime() < Date.now(),
          route: task.id === task.processId ? ['/workflows/instances', task.processId] : ['/my-work/task', task.id]
        })),
        ...pooled.map((task) => ({
          id: `pool:${task.id}`,
          kind: 'pool' as const,
          title: task.name,
          detail: task.docName || '',
          at: task.dueAt || task.startedAt,
          route: ['/my-work']
        })),
        ...expiring
          .filter((doc) => doc.status !== 'active')
          .map((doc) => ({
            id: `expiry:${doc.id}`,
            kind: 'expiry' as const,
            title: doc.name,
            detail: doc.expiry,
            at: doc.expiry,
            overdue: doc.status === 'expired',
            route: ['/document', doc.id]
          }))
      ];
      this.items.set(items);
    });
  }

  /** Everything on screen right now counts as seen — the badge clears. */
  markAllSeen(): void {
    const next = new Set(this.seenIds());
    for (const item of this.items()) {
      next.add(item.id);
    }
    this.setSeen(next);
  }

  markSeen(id: string): void {
    if (this.seenIds().has(id)) {
      return;
    }
    const next = new Set(this.seenIds());
    next.add(id);
    this.setSeen(next);
  }

  private setSeen(next: Set<string>): void {
    const capped = new Set([...next].slice(-NotificationsService.SEEN_MAX));
    this.seenIds.set(capped);
    try {
      localStorage.setItem(this.seenKey(), JSON.stringify([...capped]));
    } catch {
      // storage refused — the badge just resets next visit, nothing breaks
    }
  }

  private loadSeen(): string[] {
    try {
      const raw = localStorage.getItem(this.seenKey());
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === 'string') : [];
    } catch {
      return [];
    }
  }

  private seenKey(): string {
    return `${NotificationsService.SEEN_KEY}.${this.auth.getUsername() ?? 'anon'}`;
  }
}
